/**
 * Animation update handler
 * Handles scroll-based animation updates with throttling
 */

import {
  calculatePivotViewport,
  calculateAnimationProgress,
  recalculateViewportPositions,
} from './scroll-trigger-config'
import { calculateAnimatedTransform, TransformProps, InitialTransformProps } from './animation-helpers'

export interface AnimationUpdateContext {
  element: HTMLElement
  elementRect: DOMRect
  animationConfig: any
  parentContainer: HTMLElement
  parentHeight: number
  parentWidth: number
  transformProps: TransformProps
  initialTransformProps: InitialTransformProps
  keyframes: any[] | undefined
  scaleFromKeyframes: boolean
  rotateFromKeyframes: boolean
  pivot: 'center' | 'top' | 'bottom'
  startViewportPosition: number
  endViewportPosition: number
  useFullViewportMode: boolean
  useViewportMode: boolean
  cachedParentHeight: number
  cachedParentWidth: number
  needsRecalc: boolean
}

function buildTransformString(t: TransformProps): string {
  const x = t.x ?? 0
  const y = t.y ?? 0
  let s = `translate3d(${x}px,${y}px,0)`
  if (t.rotation !== undefined) s += ` rotate(${t.rotation}deg)`
  if (t.scale !== undefined) s += ` scale(${t.scale})`
  return s
}

/**
 * Create animation update handler
 */
export function createAnimationUpdateHandler(
  context: Omit<AnimationUpdateContext, 'cachedParentHeight' | 'cachedParentWidth' | 'needsRecalc'>,
) {
  let cachedParentHeight = context.parentHeight
  let cachedParentWidth = context.parentWidth
  let needsRecalc = false

  return {
    update: () => {
      const currentViewportHeight = window.innerHeight
      const elementRect = context.element.getBoundingClientRect()
      let currentStartViewportPosition = context.startViewportPosition
      let currentEndViewportPosition = context.endViewportPosition
      let currentPivotViewport = 0

      if (context.useFullViewportMode) {
        currentStartViewportPosition = 0
        currentEndViewportPosition = 0
        currentPivotViewport = 0
      } else {
        const recalculated = recalculateViewportPositions(
          context.animationConfig.scrollTrigger,
          currentViewportHeight,
          context.useViewportMode,
        )
        currentStartViewportPosition = recalculated.startViewportPosition
        currentEndViewportPosition = recalculated.endViewportPosition
        currentPivotViewport = calculatePivotViewport(elementRect, context.pivot)
      }

      const progress = calculateAnimationProgress(
        currentPivotViewport,
        currentStartViewportPosition,
        currentEndViewportPosition,
        context.useFullViewportMode,
        elementRect,
        currentViewportHeight,
      )

      if (needsRecalc) {
        const currentParentRect = context.parentContainer.getBoundingClientRect()
        cachedParentHeight = currentParentRect.height
        cachedParentWidth = currentParentRect.width
        needsRecalc = false
      }

      const progressPercent = progress * 100
      const animatedTransform = calculateAnimatedTransform(
        progress,
        progressPercent,
        {
          animationConfig: context.animationConfig,
          parentHeight: context.parentHeight,
          parentWidth: context.parentWidth,
          cachedParentHeight,
          cachedParentWidth,
          needsRecalc: false,
          parentContainer: context.parentContainer,
        },
        context.transformProps,
        context.initialTransformProps,
        context.keyframes,
        context.scaleFromKeyframes,
        context.rotateFromKeyframes,
        context.animationConfig.keyframeEase,
      )

      context.element.style.transform = buildTransformString(animatedTransform)
    },
    setNeedsRecalc: () => {
      needsRecalc = true
    },
  }
}

/**
 * Create throttled scroll/resize handlers
 */
export function createThrottledHandlers(
  updateAnimation: () => void,
  setNeedsRecalc: () => void,
): {
  cleanup: () => void
} {
  let rafId: number | null = null

  const handleScroll = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        updateAnimation()
        rafId = null
      })
    }
  }

  const handleResize = () => {
    setNeedsRecalc()
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        updateAnimation()
        rafId = null
      })
    }
  }

  const cleanup = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleResize, { passive: true })

  return { cleanup }
}
